import { AspectRatioIcon } from '../common/AspectRatioIcon';
import { useLayoutEffect, useMemo, useState, useEffect, useRef } from 'react';
import styles from './GenerationWorkspace.module.css';
import msStyles from '../common/MultiSelectDropdown.module.css';

import { Tooltip } from '../common/Tooltip';
import { ChevronIcon, ThreeDotsIcon } from '../common/selectIcons';
import { ListTooltip } from '../common/ListTooltip';
import { HelpTooltip } from '../common/HelpTooltip';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { AspectRatioPickerModal } from './AspectRatioPickerModal';
import { ImageModelFamilySelector } from './common/ImageModelFamilySelector';
import { HierarchicalSelect } from '../common/HierarchicalSelect';
import { SingleSelectDropdown } from '../common/SingleSelectDropdown';
import { useGenerationAssets } from './state/assetsContext';
import { useGenerationSettings } from './state/settingsContext';
import {
  GENERATION_MODEL_SPEED,
  supportsThinkingLevel,
  getThinkingLevelOptions,
  getDefaultThinkingLevel,
  resolveThinkingLevelForModel,
  type ThinkingLevel,
  DEFAULT_GENERATION_MODEL,
  normalizeOutputFormats,
  ALL_IMAGE_OUTPUT_FORMATS,
  supportsTransparency,
  isTransparencyCapableModel,
  getLastMenuForPlatform,
  getLastSpecForMenu,
} from './state/useGenerationState';
import { ASPECT_RATIO_LABELS } from '../../utils/aspectRatio';
import { fetchMediaPlatforms, fetchMediaMenus, fetchAssetSpecs, isVideoSpec, type MediaPlatform, type MediaMenu, type AssetSpec } from '../../features/media/api';
import { useGenerationModels } from '../../features/generation-models/hooks';
import { useCompliancePreview } from '../../features/compliance/api';
import { useToast } from '../../hooks/useToast';
import { selectImageModelByCode } from './utils/imageModelSelection';
import {
  gptImageFamilyDisplayName,
  gptImageFamilyLongLabel,
  gptImageFamilyShortLabel,
  gptImageFamilyTooltip,
  isGptImageModelCode,
  stripGptPrefix,
} from './utils/imageModelFamily';
import {
  buildAspectRatioSupportMessage,
  deriveCustomResolutionFromSpec,
  shouldAutoTrimBeGreyed,
  getSpecOrientation,
  getModelNativeAspectForOrientation,
  getApiOutputPixelSize,
  getModelResolutionEdge,
  findNearestAspectRatio,
  resolveSpecAspectRatio,
  getSpecGuardBounds,
  chooseFeasibleImageSize,
  evaluateRecommendedShortfall,
  getSpecRecommendedSize,
  getSpecRatioString,
  parseRatioValue,
} from './utils/aspectRatioSupport';
import {
  INITIAL_AUTO_TRIM_DEFAULT_STATE,
  evaluateAutoTrimDefault,
  markAutoTrimTouched,
  type AutoTrimDefaultState,
} from './utils/autoTrimDefaultPolicy';

// 生成画面の選択肢に出すアスペクト比 (12種)。5:4 / 21:9 は媒体規格に使用例がないため選択肢から外した (Gemini 自体は対応)。
// 1:4 / 1:8 / 4:1 / 8:1 は 3.1 Flash 系のみ対応 — モデル別の出し分けはせず、
// 非対応モデル選択時は buildAspectRatioSupportMessage の警告 + 最近傍丸めに任せる。
const ALLOWED_ASPECT_RATIOS = new Set([
  '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '9:16', '16:9',
  '4:1', '1:4', '8:1', '1:8',
]);

// アスペクト比の表示順序（固定）
const ASPECT_RATIO_ORDER = [
  '16:9', '9:16', '1:1', '4:3', '3:4', '3:2', '2:3', '4:5',
  '4:1', '1:4', '8:1', '1:8',
];

type MediaRequirementsAccordionProps = {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  mode: 'attributes' | 'prompt';
  /** ヘッダー（タイトル + chevron）を描画しない。中身だけを使いたい呼び出し側用 */
  headerless?: boolean;
  /** プリセットボタンに表示するアスペクト比リスト（未指定なら ['16:9','9:16','1:1']） */
  aspectRatioPresets?: string[];
  /** 「指定なし」プリセットを非表示にする */
  hideUnselectedRatio?: boolean;
  /** 「その他」セレクトのプレースホルダー文言（未指定なら 'その他'） */
  customSelectLabel?: string;
  /** 「その他」/カスタム セレクト自体を非表示にする */
  hideCustomAspectSelect?: boolean;
  /** 自動リサイズのチェックボックスを画面から隠す（値は常に true 扱いになる） */
  hideAutoTrim?: boolean;
  /** 思考レベル (Google Gemini スピード選択時のみモデル直下に表示) */
  thinkingLevel?: ThinkingLevel;
  onThinkingLevelChange?: (level: ThinkingLevel) => void;
};


export function MediaRequirementsAccordion({
  isOpen,
  onToggle,
  onClose,
  mode,
  headerless = false,
  aspectRatioPresets,
  hideUnselectedRatio = false,
  customSelectLabel,
  hideCustomAspectSelect = false,
  hideAutoTrim = false,
  thinkingLevel,
  onThinkingLevelChange,
}: MediaRequirementsAccordionProps) {
  const otherSelectLabel = customSelectLabel ?? 'その他';
  const { settings, setSettings } = useGenerationSettings();
  const { selectedAssets } = useGenerationAssets();
  // 選択中 assets に対するコンプラ候補と自動判定値をバックエンドから取得。
  // (商品の industry_categories / compliance_defaults はフロントから直接見えないため
  //  バックエンドが解決した結果を受け取る)
  const { data: compliancePreview } = useCompliancePreview(
    selectedAssets.map((a) => ({ id: a.id, type: a.type, asin: a.asin })),
  );
  const { data: imageModels = [] } = useGenerationModels('image');
  // 選択中モデルが解像度セレクタを提供するかを DB (supported_resolutions) から判定。
  // NULL / 空配列なら「モデル内部で解像度が決まる (e.g. OpenAI gpt-image は aspect_ratio → pixel 変換)」
  const activeModelCode = settings.generation_model || DEFAULT_GENERATION_MODEL;
  const activeModelRow = imageModels.find((m) => m.model_code === activeModelCode);
  const modelResolutions = activeModelRow?.supported_resolutions ?? null;
  const hasModelResolutions = !!modelResolutions && modelResolutions.length > 0;
  // 出力形式: 全フォーマットをボタンとして列挙し、モデルがサポートしていないものは無効化する。
  // デフォルト値は「指定なし」(settings.output_format が undefined)。
  // 指定なしの場合 payload に output_format を含めず、バックエンド側の既定値に任せる。
  const modelSupportedFormats = normalizeOutputFormats(activeModelRow?.supported_output_formats ?? null);
  const currentFormat = settings.output_format; // undefined = 指定なし
  const toast = useToast();

  // モデル切替で現在選択中の output_format が非サポートになった場合、
  // 「指定なし」にフォールバックし、ユーザーにトーストで通知。
  // activeModelCode の変化で発火させたいので previous を ref で追跡する。
  const previousModelCodeRef = useRef<string>(activeModelCode);
  useEffect(() => {
    const previousModel = previousModelCodeRef.current;
    previousModelCodeRef.current = activeModelCode;
    if (previousModel === activeModelCode) return; // 初回マウント or 同じモデル
    if (currentFormat === undefined) return;       // 指定なしならそのまま
    if (modelSupportedFormats.includes(currentFormat)) return; // 新モデルもサポートしている

    setSettings((prev) => ({ ...prev, output_format: undefined }));
    toast.show(
      `${activeModelRow?.display_label ?? activeModelCode} は ${currentFormat.toUpperCase()} をサポートしていないため、出力形式を「指定なし」に戻しました`,
    );
  }, [activeModelCode, activeModelRow?.display_label, currentFormat, modelSupportedFormats, setSettings, toast]);

  // モデル切替で「切替前の解像度が新モデルで非対応」になった場合、対応解像度へ確定
  // させてトーストで通知する。2 経路をまとめてケアする:
  //   (A) ライト/gpt-image など selectImageModelByCode の汎用分岐は image_size を触らない
  //       ため、非対応値がそのまま残る → ここでスナップする (例: 2K→ライト → 1K)。
  //   (B) スピード↔高品質は selectImageModel が保存済みサイズへ既に差し替えるが、
  //       (旧: スピード専用の 512px から高品質へ移ると黙って別解像度になる → その旨を通知する。
  //        512px は 2026-10-08 に廃止したが、保存済み設定から残る可能性があるので経路は残す)
  // 切替前の解像度が新モデルでも使える場合は、ユーザー選択が維持されるので通知しない。
  // モデルを切り替えたら思考レベルは常にそのモデルの既定に戻す (2026-10-08 利用者決定)。
  // 例: スピードで熟考 → 標準に切替 → 標準の既定「標準 (medium)」。初回マウントでは保存値を尊重する
  // (読み出し時の非対応値は resolveThinkingLevelForModel が既定へ倒す)。
  const previousModelCodeForThinkingRef = useRef<string>(activeModelCode);
  useEffect(() => {
    if (previousModelCodeForThinkingRef.current === activeModelCode) return;
    previousModelCodeForThinkingRef.current = activeModelCode;
    if (!onThinkingLevelChange || !supportsThinkingLevel(activeModelCode)) return;
    onThinkingLevelChange(getDefaultThinkingLevel(activeModelCode));
  }, [activeModelCode, onThinkingLevelChange]);

  const previousModelCodeForSizeRef = useRef<string>(activeModelCode);
  const previousImageSizeRef = useRef<string>(settings.image_size || '1K');
  useEffect(() => {
    const previousModel = previousModelCodeForSizeRef.current;
    const previousSize = previousImageSizeRef.current;
    previousModelCodeForSizeRef.current = activeModelCode;
    previousImageSizeRef.current = settings.image_size || '1K';
    if (previousModel === activeModelCode) return; // 初回マウント or 同じモデル
    if (!hasModelResolutions) return;              // 解像度セレクタ非提供モデルは対象外
    if (modelResolutions!.includes(previousSize)) return; // 旧解像度が新モデルでも有効
    const RES_EDGE: Record<string, number> = { '1K': 1280, '2K': 1920, '4K': 3840 };
    const supported = modelResolutions!
      .filter((r) => RES_EDGE[r] != null)
      .sort((a, b) => RES_EDGE[a] - RES_EDGE[b]);
    if (supported.length === 0) return;
    // 新モデルで確定する解像度。(A) では非対応値が残っているのでスナップして確定、
    // (B) では selectImageModel が既に対応解像度へ復元済みなのでその値を採用する。
    let finalSize = settings.image_size || '1K';
    if (!modelResolutions!.includes(finalSize)) {
      // previousSize 以下で最大の対応解像度を選ぶ (無ければ最小=先頭)
      finalSize = [...supported].reverse().find((r) => RES_EDGE[r] <= RES_EDGE[previousSize]) ?? supported[0];
      setSettings((prev) => ({ ...prev, image_size: finalSize }));
      // 単一解像度モデル (例: ライト=1K 固定) は永続化不要。しかも非スピードは
      // image_size_quality を Pro と共有するため、ここで書くと Pro の記憶 (2K 等) を
      // 上書きしてしまう。ユーザーに選ぶ余地がある (supported.length > 1) ときだけ保存する。
      if (supported.length > 1) {
        const sizeKey = activeModelCode === GENERATION_MODEL_SPEED ? 'image_size_speed' : 'image_size_quality';
        localStorage.setItem(sizeKey, finalSize);
      }
    }
    if (finalSize === previousSize) return; // 実際には変わっていない
    // モデルファミリー名 (ImageModelFamilySelector のグルーピングと同一規約) + 品質ラベル。
    // テキストのみなので GPT 系は「通称 (正式名称)」で出す (例: 標準 (GPT Image 2.5 Flare))。
    const familyLabel = isGptImageModelCode(activeModelCode)
      ? gptImageFamilyLongLabel(activeModelCode, imageModels)
      : 'Google Gemini';
    const label = `${familyLabel} ${activeModelRow?.display_label ?? activeModelCode}`;
    toast.show(
      supported.length === 1
        ? `${label} は ${finalSize} のみ対応のため、解像度を ${previousSize} → ${finalSize} に変更しました`
        : `${label} は ${previousSize} に非対応のため、解像度を ${previousSize} → ${finalSize} に変更しました`,
    );
  }, [
    activeModelCode,
    activeModelRow?.display_label,
    hasModelResolutions,
    modelResolutions,
    settings.image_size,
    setSettings,
    toast,
  ]);

  // 背景透過は GPT Image + PNG/WebP のみ有効。それ以外の組み合わせになったら自動で外す。
  const transparencyAvailable = supportsTransparency(activeModelCode, currentFormat);
  const currentBackground = settings.image_background;
  useEffect(() => {
    if (currentBackground === 'transparent' && !transparencyAvailable) {
      setSettings((prev) => ({ ...prev, image_background: undefined }));
    }
  }, [transparencyAvailable, currentBackground, setSettings]);

  const [mediaPlatforms, setMediaPlatforms] = useState<MediaPlatform[]>([]);
  const [mediaMenus, setMediaMenus] = useState<MediaMenu[]>([]);
  const [mediaSpecs, setMediaSpecs] = useState<AssetSpec[]>([]);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [menuLoading, setMenuLoading] = useState(false);
  const [specLoading, setSpecLoading] = useState(false);
  // 出力形式ドロップダウン (PNG/WebP で 通常/透過 を選択) の開閉状態
  const [openFormatMenu, setOpenFormatMenu] = useState<'png' | 'webp' | null>(null);
  const formatMenuContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!openFormatMenu) return;
    const handler = (e: MouseEvent) => {
      if (!formatMenuContainerRef.current) return;
      if (!formatMenuContainerRef.current.contains(e.target as Node)) {
        setOpenFormatMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openFormatMenu]);
  // WebP ボタンのレスポンシブ折りたたみ: 行幅が足りるときは PNG / JPEG と同じ通常ボタン、
  // 足りない (折り返す) ときは 3 点ボタンのメニューに格納する。
  // 通常表示で必要だった幅 (webpFullWidthRef) を覚えておき、それ以上に広がったら戻す (ヒステリシス)。
  const [webpCompact, setWebpCompact] = useState(false);
  const webpFullWidthRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = formatMenuContainerRef.current;
    if (!el) return;
    const measure = () => {
      const cs = getComputedStyle(el);
      const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
      const padding = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const children = Array.from(el.children) as HTMLElement[];
      const needed = children.reduce((w, c) => w + c.getBoundingClientRect().width, 0)
        + gap * Math.max(0, children.length - 1);
      const avail = el.clientWidth - padding;
      if (avail <= 0) return;
      if (!webpCompact) {
        if (needed > avail + 0.5) {
          webpFullWidthRef.current = needed;
          setWebpCompact(true);
        }
      } else if (webpFullWidthRef.current !== null && avail >= webpFullWidthRef.current + 0.5) {
        setWebpCompact(false);
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });



  const selectedPlatformId = settings.media_platform_id ?? null;
  const selectedMenuId = settings.media_menu_id ?? null;
  const selectedSpecId = settings.media_spec_id ?? null;

  // メニュー / 画像仕様に require_no_text / require_no_logo フラグが立っている場合、
  // 現在の生成設定が反する状態 (テキスト含めない=OFF / ロゴ追加する=ON) なら、
  // ユーザーに切り替えを促すモーダルを順番に表示する。
  // overall_impression → aspect_ratio の確認モーダル (GenerationWorkspace) と同じく、
  // ユーザーは「そのまま」を選んで推奨を適用しないことも許される。
  // 挙動も Instagram→aspect_ratio と同様、初回マウント時 (mediaMenus / mediaSpecs ロード後)
  // にも条件を満たせば発火させる。lastKey で同じメニュー/仕様の組み合わせに対して二重発火を防ぐ。
  const [mediaCompliancePrompts, setMediaCompliancePrompts] = useState<Array<'text' | 'logo'>>([]);
  const currentCompliancePrompt = mediaCompliancePrompts[0] ?? null;
  const evaluatedComplianceKeyRef = useRef<{ menuId: string | null; specId: string | null } | null>(null);
  useEffect(() => {
    const menuId = selectedMenuId;
    const specId = selectedSpecId;
    const lastKey = evaluatedComplianceKeyRef.current;
    if (lastKey && lastKey.menuId === menuId && lastKey.specId === specId) {
      return;
    }
    // メニュー/仕様が選択されているのにデータ未ロードならスキップ。ロード完了後の再レンダリングで再評価される
    const menu = menuId ? mediaMenus.find((m) => m.id === menuId) : null;
    const spec = specId ? mediaSpecs.find((s) => s.id === specId) : null;
    if (menuId && !menu) return;
    if (specId && !spec) return;

    evaluatedComplianceKeyRef.current = { menuId, specId };

    const imageSpec = spec && !isVideoSpec(spec) ? spec : null;
    const requireNoText = !!(menu?.require_no_text || imageSpec?.require_no_text);
    const requireNoLogo = !!(menu?.require_no_logo || imageSpec?.require_no_logo);

    const prompts: Array<'text' | 'logo'> = [];
    if (requireNoText && !settings.exclude_text_response) prompts.push('text');
    if (requireNoLogo && settings.include_logo) prompts.push('logo');
    if (prompts.length > 0) {
      setMediaCompliancePrompts(prompts);
    }
    // 設定値 (exclude_text_response / include_logo) は意図的に依存配列から除く。
    // 含めるとユーザーが設定を変更するたびに再評価されるが、メニュー/仕様が変わっていないなら再プロンプトしたくない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenuId, selectedSpecId, mediaMenus, mediaSpecs]);

  const dismissCurrentCompliancePrompt = () => {
    setMediaCompliancePrompts((prev) => prev.slice(1));
  };
  const acceptTextCompliancePrompt = () => {
    setSettings((prev) => ({ ...prev, exclude_text_response: true }));
    dismissCurrentCompliancePrompt();
  };
  const acceptLogoCompliancePrompt = () => {
    setSettings((prev) => ({ ...prev, include_logo: false }));
    dismissCurrentCompliancePrompt();
  };

  // 媒体要件の設定サマリー
  // アコーディオン内の全項目 (プラットフォーム / メニュー / 出力画像 / アスペクト比 /
  // 自動リサイズ & 目標px / モデル / 解像度 / 出力形式) を可能な限り反映する。
  const mediaRequirementsSummary = useMemo(() => {
    const items: string[] = [];

    if (selectedPlatformId) {
      const platform = mediaPlatforms.find((p) => p.id === selectedPlatformId);
      if (platform) items.push(platform.display_name);
    }

    if (selectedMenuId) {
      const menu = mediaMenus.find((m) => m.id === selectedMenuId);
      if (menu) items.push(menu.display_name);
    }

    if (selectedSpecId) {
      const spec = mediaSpecs.find((s) => s.id === selectedSpecId);
      if (spec) items.push(spec.display_name);
    }

    if (settings.aspect_ratio && settings.aspect_ratio !== 'none') {
      items.push(`${settings.aspect_ratio}`);
    }

    // 自動リサイズ / 自動トリミング (OFF のときのみ明示)
    if (settings.auto_trim_resolution === false) {
      items.push('リサイズOFF');
    } else if (settings.target_width && settings.target_height) {
      items.push(`${settings.target_width}×${settings.target_height}px`);
    }

    // 生成モデル: GPT Image 系はファミリー名を略称 ("GPT標準" / "GPT長時間" / 履歴の旧 "GPTi2") で表示し、
    // 品質トークンを別項目として並べる (高品質 / 標準 / 低品質 はそのまま、
    // 'auto' だけは単独の "自動" だと意味が通じないため "品質自動" にする)。
    // Gemini 側は GPT と同じく "Gemini" + 品質名 (例: 'Gemini標準'。display_label の旧 'モード' 接尾辞は落とす)。
    if (activeModelRow) {
      if (isGptImageModelCode(activeModelRow.model_code)) {
        items.push(gptImageFamilyShortLabel(activeModelRow.model_code));
        const rawQuality = stripGptPrefix(activeModelRow.display_label);
        const quality = rawQuality === '自動' ? '品質自動' : rawQuality;
        if (quality) items.push(quality);
      } else {
        items.push(`Gemini${activeModelRow.display_label.replace(/モード$/, '')}`);
      }
    }

    if (settings.image_size && hasModelResolutions) {
      items.push(settings.image_size);
    }

    if (currentFormat) {
      const fmtLabel = currentFormat === 'webp' ? 'WebP' : currentFormat.toUpperCase();
      items.push(currentBackground === 'transparent' ? `透過${fmtLabel}` : fmtLabel);
    }

    return items.length > 0 ? items.join(', ') : '設定なし';
  }, [
    selectedPlatformId,
    selectedMenuId,
    selectedSpecId,
    settings.aspect_ratio,
    settings.image_size,
    settings.auto_trim_resolution,
    settings.target_width,
    settings.target_height,
    activeModelRow,
    hasModelResolutions,
    currentFormat,
    currentBackground,
    mediaPlatforms,
    mediaMenus,
    mediaSpecs,
  ]);

  const handlePlatformChange = (platformId: string | null) => {
    // プラットフォーム切替時、過去にそのプラットフォームで選択していた menu / spec を復元する。
    // 妥当性のチェックは menu / spec の fetch effect 側で実施され、無効なら is_default にフォールバックする。
    const restoredMenuId = platformId ? getLastMenuForPlatform(platformId) ?? null : null;
    const restoredSpecId = restoredMenuId ? getLastSpecForMenu(restoredMenuId) ?? null : null;
    setSettings((prev) => ({
      ...prev,
      media_platform_id: platformId,
      media_menu_id: restoredMenuId,
      media_spec_id: restoredSpecId,
      media_guidelines: [],
      media_menu_guidelines: [],
    }));
  };

  const handleMenuChange = (menuId: string | null) => {
    const menu = menuId ? mediaMenus.find((m) => m.id === menuId) : null;
    // メニュー切替時、過去にそのメニューで選択していた spec を復元。妥当性は spec fetch effect 側で再評価。
    const restoredSpecId = menuId ? getLastSpecForMenu(menuId) ?? null : null;
    setSettings((prev) => {
      let aspectRatio = prev.aspect_ratio;
      if (menuId && aspectRatio && aspectRatio !== 'none') {
        // 新しいmenuのspecsがまだ読み込まれていない場合は、次のeffectでリセットされる
      } else if (!menuId) {
        aspectRatio = 'none';
      }

      return {
        ...prev,
        media_menu_id: menuId,
        media_spec_id: restoredSpecId,
        media_guidelines: [],
        media_menu_guidelines: menu?.guidelines || [],
        aspect_ratio: aspectRatio,
      };
    });
  };

  const handleSpecChange = (specId: string | null) => {
    const spec = mediaSpecs.find((item) => item.id === specId) || null;
    setSettings((prev) => ({
      ...prev,
      media_spec_id: specId,
      media_guidelines: spec?.guidelines || [],
    }));
  };

  // プラットフォーム一覧は backend (partner 単位の exclusion/inclusion) でフィルタ済みのものをそのまま使用
  const platformOptions = useMemo(
    () => mediaPlatforms.map((platform) => ({ id: platform.id, label: platform.display_name })),
    [mediaPlatforms],
  );

  const menuOptions = useMemo(
    () => mediaMenus
      // MediaRequirementsAccordion は画像生成フロー専用。動画メニュー (mediaType==='video') は除外する。
      .filter((menu) => menu.media_type !== 'video')
      .map((menu) => ({
        id: menu.id,
        label: menu.display_name,
        mediaType: menu.media_type,
        guidelines: menu.guidelines || [],
      })),
    [mediaMenus],
  );

  const specOptions = useMemo(
    () =>
      mediaSpecs.map((spec) => {
        // 推奨サイズがあればガイドラインの先頭に併記する (最小 / 推奨の区別を選択時に見せる)
        const rec = getSpecRecommendedSize(spec);
        const recLine = rec
          ? (spec.size_constraint_type === 'longest_edge_range'
            ? `推奨長辺: ${rec.width}px`
            : `推奨サイズ: ${rec.width}×${rec.height}px`)
          : null;
        // 比率と解像度は名前に書かず、構造化データから出す
        // (比率: allowed_aspect_ratios[0] → 無ければ W×H から導出)
        // 解像度タグ: fixed は塗り "1200×628" (その寸法で出力)、min 系は枠線のみ "600×500 以上" (下限)。
        // 片辺しか無い最小 (Meta の最小幅500 等) は比率から残りを導出する
        const ratio = getSpecRatioString(spec);
        const t = spec.size_constraint_type;
        let sizeTag: { text: string; variant: 'filled' | 'outline' } | undefined;
        if (t === 'fixed' && spec.width_px && spec.height_px) {
          sizeTag = { text: `${spec.width_px}×${spec.height_px}`, variant: 'filled' };
        } else if (t === 'min' || t === 'min_with_aspect' || t === 'range') {
          let w = spec.min_width_px ?? null;
          let h = spec.min_height_px ?? null;
          const r = ratio ? parseRatioValue(ratio) : null;
          if (w && !h && r) h = Math.round(w / r);
          if (h && !w && r) w = Math.round(h * r);
          if (w && h) sizeTag = { text: `${w}×${h} 以上`, variant: 'outline' };
        }
        return {
          id: spec.id,
          label: spec.display_name,
          description: spec.description,
          origin: spec.origin,
          guidelines: recLine ? [recLine, ...(spec.guidelines || [])] : (spec.guidelines || []),
          ratio,
          sizeTag,
        };
      }),
    [mediaSpecs],
  );

  const selectedMenu = useMemo(() => {
    if (!selectedMenuId) return null;
    return mediaMenus.find((menu) => menu.id === selectedMenuId) || null;
  }, [mediaMenus, selectedMenuId]);

  const currentMenuGuidelines = selectedMenu?.guidelines || [];
  const currentSpecGuidelines = settings.media_guidelines || [];

  const availableAspectRatios = useMemo(() => {
    if (mode === 'prompt') {
      return ASPECT_RATIO_ORDER.filter((ratio) => ALLOWED_ASPECT_RATIOS.has(ratio));
    }

    if (selectedMenuId && mediaSpecs.length > 0) {
      const ratios = new Set<string>();
      mediaSpecs.forEach((spec) => {
        if (spec.allowed_aspect_ratios && spec.allowed_aspect_ratios.length > 0) {
          spec.allowed_aspect_ratios.forEach((ratio) => {
            if (ratio && ratio.trim()) {
              ratios.add(ratio.trim());
            }
          });
        }
      });

      if (ratios.size > 0) {
        return ASPECT_RATIO_ORDER.filter((ratio) => ratios.has(ratio));
      }
    }

    return ASPECT_RATIO_ORDER.filter((ratio) => ALLOWED_ASPECT_RATIOS.has(ratio));
  }, [mode, selectedMenuId, mediaSpecs]);

  // 選択中の spec 実体（allowed_aspect_ratios / size_constraint_type を参照するため）
  const selectedSpec = useMemo<AssetSpec | null>(
    () => mediaSpecs.find((s) => s.id === selectedSpecId) ?? null,
    [mediaSpecs, selectedSpecId],
  );

  // spec の向き (landscape / portrait / square) を判定
  const specOrientation = useMemo(() => getSpecOrientation(selectedSpec), [selectedSpec]);

  // アスペクト比プリセット:
  //   1. 明示 prop (aspectRatioPresets) が最優先
  //   2. spec 選択時:
  //      - gpt-image-2: spec の実寸法から算出した比率 (任意比率をネイティブ出力可能)
  //      - Gemini: spec の向きに対応するモデルネイティブ比 (旧来挙動)
  //   3. spec 未選択時は共通で 16:9/9:16/1:1/1.91:1 を提示
  const presets = useMemo<string[]>(() => {
    if (aspectRatioPresets && aspectRatioPresets.length > 0) return aspectRatioPresets;
    if (specOrientation) {
      const specRatio = resolveSpecAspectRatio(activeModelCode, selectedSpec);
      if (specRatio) return [specRatio];
      return [getModelNativeAspectForOrientation(activeModelCode, specOrientation)];
    }
    return ['16:9', '9:16', '1:1', '1.91:1'];
  }, [aspectRatioPresets, specOrientation, activeModelCode, selectedSpec]);

  // spec 選択時は「指定なし」「その他」ともに非表示 (spec × モデルで比率は一意に決まるため)
  const effectiveHideCustomAspectSelect = hideCustomAspectSelect || !!selectedSpec;
  const effectiveHideUnselectedRatio = hideUnselectedRatio || !!selectedSpec;

  // アスペクト比一覧モーダル (「その他」クリックで全比率をビジュアル表示)
  const [aspectRatioModalOpen, setAspectRatioModalOpen] = useState(false);

  // モーダルのヘッダーに出すモデル表示名: 「Google Gemini - 高品質」形式。
  // ファミリー名は ImageModelFamilySelector の表記 (通称: 標準 / 長時間、Google Gemini) と
  // 揃え、品質部分は display_label から冗長な接頭辞/接尾辞 (GPT / モード) を落とす。
  // GPT 系で通称を出しているときは正式名称をツールチップ (modalModelTooltip) で添える。
  const modalModelLabel = useMemo(() => {
    const isOpenAI = isGptImageModelCode(activeModelCode);
    const family = isOpenAI ? `GPT ${gptImageFamilyDisplayName(activeModelCode, imageModels)}` : 'Google Gemini';
    const raw = activeModelRow?.display_label ?? '';
    const quality = isOpenAI ? stripGptPrefix(raw) : raw.replace(/モード$/, '');
    return quality ? `${family} - ${quality}` : family;
  }, [activeModelCode, activeModelRow, imageModels]);
  const modalModelTooltip = useMemo(
    () => (isGptImageModelCode(activeModelCode) ? gptImageFamilyTooltip(activeModelCode, imageModels) : null),
    [activeModelCode, imageModels],
  );

  // モーダルに出す一覧: 選択可能な全比率 + プリセット (1.91:1 等 ORDER 外のものを含む) の和集合を、
  // 横長 → 正方形 → 縦長 (数値比の降順) で並べる。形状プレビューの比較がしやすい順序にする。
  const modalRatios = useMemo(() => {
    const merged = [...new Set([...availableAspectRatios, ...presets])]
      .filter((ratio) => ratio && ratio !== 'none');
    const ratioValue = (ratio: string) => {
      const [w, h] = ratio.split(':').map(Number);
      return Number.isFinite(w) && Number.isFinite(h) && h > 0 ? w / h : 0;
    };
    return merged.sort((a, b) => ratioValue(b) - ratioValue(a));
  }, [availableAspectRatios, presets]);

  // モデル非対応時の警告メッセージ (null なら非表示)
  const aspectRatioWarningMessage = useMemo(
    () => buildAspectRatioSupportMessage(activeModelCode, settings.aspect_ratio),
    [activeModelCode, settings.aspect_ratio],
  );

  // spec 切替時に target_width/height を spec 値で自動入力。
  // ユーザーが手動編集したあとに spec を再選択しても、spec のピクセル値で上書きする (プリセット優先)。
  const previousSpecIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prevSpecId = previousSpecIdRef.current;
    if (prevSpecId === selectedSpecId) return; // 同じ spec のまま
    // spec 実体のロード待ち (ref を進めず、ロード完了後の再実行で評価する)
    if (selectedSpecId && !selectedSpec) return;
    previousSpecIdRef.current = selectedSpecId;
    if (!selectedSpec) {
      // spec 解除 → カスタム解像度もクリア
      setSettings((prev) => (prev.target_width || prev.target_height
        ? { ...prev, target_width: undefined, target_height: undefined }
        : prev));
      return;
    }
    const { width, height } = deriveCustomResolutionFromSpec(selectedSpec);
    // 導出結果が空 (比率なしの longest_edge_range 等) でも前の spec の目標を残さずクリアする。
    // 残すと auto_trim ON で古い寸法へ加工されてしまう
    // (docs/requirements/media-spec-recommended-size-warning-plan.md §3.4)。
    setSettings((prev) => (
      prev.target_width === width && prev.target_height === height
        ? prev
        : { ...prev, target_width: width, target_height: height }
    ));
  }, [selectedSpecId, selectedSpec, setSettings]);

  // auto_trim_resolution のデフォルト値を (spec, model) の組で管理する。
  // min/max 系 spec は OFF (境界は目標値ではなくガード)、fixed / spec 比率を
  // モデルがネイティブ出力できない場合は ON (従来の exact リサイズ)。
  // 遷移ルールは evaluateAutoTrimDefault (純関数) に集約:
  // 初回評価 (localStorage 復元 spec を含む)・spec 切替・モデル切替・手動変更保持。
  const autoTrimDefaultStateRef = useRef<AutoTrimDefaultState>(INITIAL_AUTO_TRIM_DEFAULT_STATE);
  useEffect(() => {
    const result = evaluateAutoTrimDefault({
      state: autoTrimDefaultStateRef.current,
      specId: selectedSpecId ?? null,
      spec: selectedSpec ?? null,
      modelCode: activeModelCode,
      currentValue: settings.auto_trim_resolution,
    });
    autoTrimDefaultStateRef.current = result.state;
    if (result.nextValue === null) return;
    const def = result.nextValue;
    setSettings((prev) => (
      (prev.auto_trim_resolution !== false) === def ? prev : { ...prev, auto_trim_resolution: def }
    ));
  }, [selectedSpecId, selectedSpec, activeModelCode, settings.auto_trim_resolution, setSettings]);

  // spec / モデル切替時に aspect_ratio を自動追従する。
  // - Gemini: spec の向きに対応するモデルネイティブ比 (16:9 等)
  // - gpt-image-2: spec の実寸法から導出した比率 (クロップ不要で生成)
  useEffect(() => {
    if (!specOrientation) return;
    const target = resolveSpecAspectRatio(activeModelCode, selectedSpec)
      ?? getModelNativeAspectForOrientation(activeModelCode, specOrientation);
    if (settings.aspect_ratio !== target) {
      setSettings((prev) => ({ ...prev, aspect_ratio: target }));
      localStorage.setItem('aspect_ratio', target);
    }
  }, [specOrientation, activeModelCode, selectedSpec, settings.aspect_ratio, setSettings]);

  // spec が要求するピクセル数に対して現在の image_size が不足している場合、
  // 自動的に最小充足サイズへ引き上げ + トーストで通知する。
  // (例: Amazon DSP 1200x628 spec + 1K 選択中で 1280px 超を要求 → 2K に引上げ)
  // min/max 系 (ガード対象) spec は下の調停 effect が担当するためここでは扱わない。
  useEffect(() => {
    if (!selectedSpec || !modelResolutions || modelResolutions.length === 0) return;
    if (getSpecGuardBounds(selectedSpec)) return;
    const RES_EDGE: Record<string, number> = { '1K': 1280, '2K': 1920, '4K': 3840 };
    const required = Math.max(
      selectedSpec.width_px ?? selectedSpec.min_width_px ?? 0,
      selectedSpec.height_px ?? selectedSpec.min_height_px ?? 0,
    );
    if (required <= 0) return;
    const current = settings.image_size || '1K';
    const currentEdge = RES_EDGE[current] ?? 0;
    if (currentEdge >= required) return;
    // モデルがサポートする解像度の中から、required 以上で最小のものを選ぶ
    const sorted = modelResolutions
      .filter((r) => RES_EDGE[r] != null)
      .sort((a, b) => RES_EDGE[a] - RES_EDGE[b]);
    const upgrade = sorted.find((r) => RES_EDGE[r] >= required);
    if (!upgrade || upgrade === current) return;
    setSettings((prev) => ({ ...prev, image_size: upgrade }));
    const sizeKey = settings.generation_model === GENERATION_MODEL_SPEED ? 'image_size_speed' : 'image_size_quality';
    localStorage.setItem(sizeKey, upgrade);
    toast.show(`解像度を${current}→${upgrade}に引き上げました`);
  }, [selectedSpec, modelResolutions, settings.image_size, settings.generation_model, setSettings, toast]);

  // spec 切替時、要求ピクセルに対して過剰な解像度なら最小充足サイズへ引き下げる (ポイント節約)。
  // 生成後は spec の実寸法へ自動リサイズされるため、要求以上の解像度で生成しても無駄になる。
  // 引き上げ (上の effect) と違い継続監視はしない — 切替後にユーザーが手動で
  // 高い解像度を選び直した場合はそれを尊重する。長辺はモデル別
  // (Gemini 1K=1280 / gpt-image-2 1K=1024) の実値で判定する。
  const downsizedSpecIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedSpecId) {
      downsizedSpecIdRef.current = null;
      return;
    }
    // spec 実体のロード待ち (ref を進めず、ロード完了後の再実行で評価する)
    if (!selectedSpec) return;
    // min/max 系 (ガード対象) spec では引き下げない: min 以上の出力はリサイズされず
    // そのまま納品されるため「過剰解像度は無駄」の前提が成り立たない (ユーザーの選択を尊重)。
    if (getSpecGuardBounds(selectedSpec)) return;
    if (downsizedSpecIdRef.current === selectedSpecId) return;
    downsizedSpecIdRef.current = selectedSpecId;
    if (!modelResolutions || modelResolutions.length === 0) return;
    const required = Math.max(
      selectedSpec.width_px ?? selectedSpec.min_width_px ?? 0,
      selectedSpec.height_px ?? selectedSpec.min_height_px ?? 0,
    );
    if (required <= 0) return;
    const current = settings.image_size || '1K';
    const currentEdge = getModelResolutionEdge(activeModelCode, current) ?? 0;
    const isSpeedMode = settings.generation_model === GENERATION_MODEL_SPEED;
    const candidates = modelResolutions
      .filter((r) => getModelResolutionEdge(activeModelCode, r) != null)
      .sort((a, b) => getModelResolutionEdge(activeModelCode, a)! - getModelResolutionEdge(activeModelCode, b)!);
    const minimal = candidates.find((r) => getModelResolutionEdge(activeModelCode, r)! >= required);
    if (!minimal || minimal === current) return;
    if (getModelResolutionEdge(activeModelCode, minimal)! >= currentEdge) return; // 引き下げのみ
    setSettings((prev) => ({ ...prev, image_size: minimal }));
    const sizeKey = isSpeedMode ? 'image_size_speed' : 'image_size_quality';
    localStorage.setItem(sizeKey, minimal);
    toast.show(`解像度を${current}→${minimal}に引き下げました`);
  }, [selectedSpecId, selectedSpec, modelResolutions, activeModelCode, settings.image_size, settings.generation_model, setSettings, toast]);

  // min/max 系 (ガード対象) spec の解像度調停。
  // 現在のバケットの予測出力が spec の [min, max] を満たさず、満たすバケットが存在する
  // 場合のみ最も近いものへ変更 + トースト。存在しなければ現状維持し、生成後の寸法ガード
  // (backend) に委ねる。変更先は必ず feasible なので往復・トーストループは起きない。
  useEffect(() => {
    if (!selectedSpec || !modelResolutions || modelResolutions.length === 0) return;
    const bounds = getSpecGuardBounds(selectedSpec);
    if (!bounds) return;
    const current = settings.image_size || '1K';
    const isSpeedMode = settings.generation_model === GENERATION_MODEL_SPEED;
    const candidates = modelResolutions
      .filter((r) => getModelResolutionEdge(activeModelCode, r) != null);
    const next = chooseFeasibleImageSize({
      modelCode: activeModelCode,
      aspectRatio: settings.aspect_ratio,
      currentSize: current,
      candidateSizes: candidates,
      bounds,
    });
    if (!next) return;
    setSettings((prev) => ({ ...prev, image_size: next }));
    const sizeKey = isSpeedMode ? 'image_size_speed' : 'image_size_quality';
    localStorage.setItem(sizeKey, next);
    const currentEdge = getModelResolutionEdge(activeModelCode, current) ?? 0;
    const nextEdge = getModelResolutionEdge(activeModelCode, next) ?? 0;
    toast.show(`解像度を${current}→${next}に${nextEdge >= currentEdge ? '引き上げ' : '引き下げ'}ました`);
  }, [selectedSpec, modelResolutions, activeModelCode, settings.image_size, settings.aspect_ratio, settings.generation_model, setSettings, toast]);

  // 媒体規格の推奨サイズに届かないときのインライン警告 (生成はブロックしない)。
  // 既存の必須判定 (ボタン無効化・調停) が先に効き、ここは推奨だけを見る。
  // docs/requirements/media-spec-recommended-size-warning-plan.md §3.3 / §3.4
  const recommendedShortfall = useMemo(() => {
    if (!selectedSpec || !modelResolutions || modelResolutions.length === 0) return null;
    return evaluateRecommendedShortfall({
      modelCode: activeModelCode,
      aspectRatio: settings.aspect_ratio,
      currentSize: settings.image_size || '1K',
      candidateSizes: modelResolutions,
      spec: selectedSpec,
      autoTrim: settings.auto_trim_resolution !== false,
      target: { width: settings.target_width, height: settings.target_height },
    });
  }, [selectedSpec, modelResolutions, activeModelCode, settings.aspect_ratio, settings.image_size, settings.auto_trim_resolution, settings.target_width, settings.target_height]);

  const applyImageSize = (size: string) => {
    setSettings((prev) => ({ ...prev, image_size: size }));
    const sizeKey = settings.generation_model === GENERATION_MODEL_SPEED ? 'image_size_speed' : 'image_size_quality';
    localStorage.setItem(sizeKey, size);
  };

  // auto-trim がグレーアウト対象か (チェック自体は操作可能だが、実際には発動しない)
  const autoTrimGreyed = useMemo(
    () => shouldAutoTrimBeGreyed({
      modelCode: activeModelCode,
      userAspectRatio: settings.aspect_ratio,
      spec: selectedSpec,
      customWidth: settings.target_width,
      customHeight: settings.target_height,
    }),
    [activeModelCode, settings.aspect_ratio, selectedSpec, settings.target_width, settings.target_height],
  );

  // プラットフォーム取得
  useEffect(() => {
    let cancelled = false;
    setPlatformLoading(true);
    fetchMediaPlatforms()
      .then((items) => {
        if (cancelled) return;
        setMediaPlatforms(items);
        setSettings((prev) => {
          if (prev.media_platform_id || items.length !== 1) {
            return prev;
          }
          return {
            ...prev,
            media_platform_id: items[0].id,
          };
        });
      })
      .catch((error) => {
        console.error('failed to load media platforms for generation', error);
      })
      .finally(() => {
        if (!cancelled) {
          setPlatformLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [setSettings]);

  // メニュー取得
  useEffect(() => {
    if (!selectedPlatformId) {
      setMediaMenus([]);
      setSettings((prev) => ({
        ...prev,
        media_menu_id: null,
        media_spec_id: null,
        media_guidelines: [],
        media_menu_guidelines: [],
      }));
      return;
    }
    let cancelled = false;
    setMenuLoading(true);
    fetchMediaMenus(selectedPlatformId)
      .then((items) => {
        if (cancelled) return;
        setMediaMenus(items);
        setSettings((prev) => {
          if (prev.media_platform_id !== selectedPlatformId) {
            return prev;
          }
          if (prev.media_menu_id && items.some((item) => item.id === prev.media_menu_id)) {
            // 既存のメニューが有効な場合、menu_guidelinesを更新
            const existingMenu = items.find((item) => item.id === prev.media_menu_id);
            return {
              ...prev,
              media_menu_guidelines: existingMenu?.guidelines || [],
            };
          }
          // 優先順位: 過去にこのプラットフォームで最後に選択したメニュー → is_default → メニューが 1 件だけ
          const lastMenuId = getLastMenuForPlatform(selectedPlatformId);
          const lastMenu = lastMenuId ? items.find((item) => item.id === lastMenuId) : undefined;
          const fallbackMenu =
            lastMenu ??
            items.find((item) => item.is_default) ??
            (items.length === 1 ? items[0] : undefined);
          if (!fallbackMenu) {
            return {
              ...prev,
              media_menu_id: null,
              media_spec_id: null,
              media_guidelines: [],
              media_menu_guidelines: [],
            };
          }
          // メニューが切り替わるので、そのメニューで最後に選んだ spec も復元 (spec fetch effect 側で再評価される)
          const restoredSpecId = getLastSpecForMenu(fallbackMenu.id) ?? null;
          return {
            ...prev,
            media_menu_id: fallbackMenu.id,
            media_spec_id: restoredSpecId,
            media_guidelines: [],
            media_menu_guidelines: fallbackMenu.guidelines || [],
          };
        });
      })
      .catch((error) => {
        console.error('failed to load media menus for generation', error);
      })
      .finally(() => {
        if (!cancelled) {
          setMenuLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPlatformId, setSettings]);

  // スペック取得
  useEffect(() => {
    if (!selectedMenuId) {
      setMediaSpecs([]);
      setSettings((prev) => ({
        ...prev,
        media_spec_id: null,
        media_guidelines: [],
      }));
      return;
    }
    let cancelled = false;
    setSpecLoading(true);
    fetchAssetSpecs(selectedMenuId)
      .then((items) => {
        if (cancelled) return;
        setMediaSpecs(items);

        setSettings((prev) => {
          if (prev.media_menu_id !== selectedMenuId) {
            return prev;
          }

          // プロンプトモードではアスペクト比の自動設定をスキップ
          if (mode === 'prompt') {
            if (prev.media_spec_id && items.some((item) => item.id === prev.media_spec_id)) {
              const currentSpec = items.find((item) => item.id === prev.media_spec_id);
              return {
                ...prev,
                media_guidelines: currentSpec?.guidelines || [],
              };
            }
            // 優先順位: 過去にこのメニューで最後に選択した spec → is_default → 先頭
            const lastSpecId = getLastSpecForMenu(selectedMenuId);
            const lastSpec = lastSpecId ? items.find((item) => item.id === lastSpecId) : undefined;
            const fallbackSpec = lastSpec ?? items.find((item) => item.is_default) ?? items[0];
            return {
              ...prev,
              media_spec_id: fallbackSpec ? fallbackSpec.id : null,
              media_guidelines: fallbackSpec?.guidelines || [],
            };
          }

          // 属性選択モード: 新しいmenuのspecsからアスペクト比を抽出
          const availableRatios = new Set<string>();
          items.forEach((spec) => {
            if (spec.allowed_aspect_ratios && spec.allowed_aspect_ratios.length > 0) {
              spec.allowed_aspect_ratios.forEach((ratio) => {
                if (ratio && ratio.trim()) {
                  availableRatios.add(ratio.trim());
                }
              });
            }
          });

          const sortedRatios = Array.from(availableRatios).sort((a, b) => {
            const parseRatio = (ratio: string): number => {
              const [w, h] = ratio.split(':').map(Number);
              if (!Number.isFinite(w) || !Number.isFinite(h) || h === 0) return 0;
              return w / h;
            };
            return parseRatio(a) - parseRatio(b);
          });

          let aspectRatio = prev.aspect_ratio;
          if (aspectRatio && aspectRatio !== 'none' && !availableRatios.has(aspectRatio)) {
            aspectRatio = sortedRatios.length > 0 ? sortedRatios[0] : 'none';
          } else if (!aspectRatio || aspectRatio === 'none') {
            aspectRatio = sortedRatios.length > 0 ? sortedRatios[0] : 'none';
          }

          if (prev.media_spec_id && items.some((item) => item.id === prev.media_spec_id)) {
            const currentSpec = items.find((item) => item.id === prev.media_spec_id);
            return {
              ...prev,
              media_guidelines: currentSpec?.guidelines || [],
              aspect_ratio: aspectRatio,
            };
          }
          // 優先順位: 過去にこのメニューで最後に選択した spec → is_default → 先頭
          const lastSpecId = getLastSpecForMenu(selectedMenuId);
          const lastSpec = lastSpecId ? items.find((item) => item.id === lastSpecId) : undefined;
          const fallbackSpec = lastSpec ?? items.find((item) => item.is_default) ?? items[0];
          return {
            ...prev,
            media_spec_id: fallbackSpec ? fallbackSpec.id : null,
            media_guidelines: fallbackSpec?.guidelines || [],
            aspect_ratio: aspectRatio,
          };
        });
      })
      .catch((error) => {
        console.error('failed to load media specs for generation', error);
      })
      .finally(() => {
        if (!cancelled) {
          setSpecLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMenuId, setSettings]);

  const renderGuidelineChip = (label: string, items: string[]) => {
    const filteredItems = items.filter(Boolean);
    if (!filteredItems.length) {
      return null;
    }
    return (
      <ListTooltip items={filteredItems} title={label}>
        <span className={styles.guidelineChip}>
          {label}
        </span>
      </ListTooltip>
    );
  };

  // コンプラルールごとボタン: 組織マスタースイッチ (effective_compliance_enabled) が
  // ON で、候補ルールが 1 つ以上あるときに表示。
  const complianceMasterEnabled = compliancePreview?.effective_compliance_enabled ?? true;
  const candidateRulesets = compliancePreview?.candidate_rulesets ?? [];
  const autoActiveCodes = useMemo(
    () => new Set(compliancePreview?.auto_active_codes ?? []),
    [compliancePreview?.auto_active_codes],
  );
  const userOverrides = settings.compliance_overrides ?? {};

  const isRuleOn = (code: string): boolean => {
    const userVal = userOverrides[code];
    if (typeof userVal === 'boolean') return userVal;
    return autoActiveCodes.has(code);
  };

  const handleToggleRule = (code: string) => {
    const next = !isRuleOn(code);
    setSettings((prev) => ({
      ...prev,
      compliance_overrides: { ...(prev.compliance_overrides ?? {}), [code]: next },
    }));
  };

  const sectionClass = headerless ? '' : `accordion-section ${isOpen ? 'accordion-section-open' : ''}`;
  const panelClass = headerless
    ? ''
    : `accordion-panel ${isOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`;

  return (
    <div className={sectionClass}>
      {!headerless && (
        <button
          type="button"
          className="accordion-header"
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          <span className="accordion-title">
            <img src="/file_code_check.svg" alt="" className="accordion-title-icon" />
            <span>媒体要件</span>
            <span className="accordion-summary">{mediaRequirementsSummary}</span>
          </span>
          <span className={`accordion-icon ${isOpen ? 'accordion-icon-open' : ''}`}>
            {isOpen ? '▼' : '▶'}
          </span>
        </button>
      )}
      <div className={panelClass}>
        {/* 2 列構成: 左列=媒体要件 (プラットフォーム/メニュー/出力画像/アスペクト比) / 右列=モデル関連 (モデル/解像度/出力形式)
            form-grid-max-2 は container 幅に応じて auto-fit で 1/2 列を切替 (素材パネル展開時やモバイル表示で自動的に折り返す) */}
        <div className="form-grid-max-2" style={{ alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="gen-option-group">
          <span className="gen-option-group-label">プラットフォーム</span>
          <div className={styles.optionGroupButtons}>
            <button
              type="button"
              className={`${styles.optionButton} ${selectedPlatformId === null ? styles.optionButtonActive : ''}`}
              onClick={() => handlePlatformChange(null)}
              disabled={platformLoading}
            >
              指定なし
            </button>
            {platformOptions.map((platform) => (
              <button
                key={platform.id}
                type="button"
                className={`${styles.optionButton} ${selectedPlatformId === platform.id ? styles.optionButtonActive : ''}`}
                onClick={() => handlePlatformChange(platform.id)}
                disabled={platformLoading}
              >
                {platform.label}
              </button>
            ))}
          </div>
          {platformLoading ? <span className={styles.helperText}>プラットフォームを読み込み中…</span> : null}
        </div>
        <div className={`${styles.nestedSection} ${selectedPlatformId ? styles.nestedSectionVisible : styles.nestedSectionHidden}`}>
          <div className="gen-label">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              メニュー
              <HelpTooltip contentKey="mediaMenu" ariaLabel="メニューのヘルプ" />
            </span>
            <SingleSelectDropdown
              label="選択してください"
              labelOnly
              alwaysShowSelected
              fitContent
              className={styles.mediaSelectFit}
              value={selectedMenuId ?? ''}
              onChange={(v) => handleMenuChange(v || null)}
              disabled={!selectedPlatformId || menuLoading}
              options={[
                { value: '', label: '選択してください' },
                ...menuOptions.map((menu) => ({ value: menu.id, label: menu.label })),
              ]}
            />
            {menuLoading ? <span className={styles.helperText}>メニューを読み込み中…</span> : null}
          </div>
          <div className="gen-label">
            <span>出力画像</span>
            <SingleSelectDropdown
              label="未選択"
              labelOnly
              alwaysShowSelected
              fitContent
              className={styles.mediaSelectFit}
              value={selectedSpecId ?? ''}
              onChange={(v) => handleSpecChange(v || null)}
              disabled={!selectedMenuId || specLoading || specOptions.length === 0}
              options={[
                { value: '', label: '未選択' },
                ...specOptions.map((spec) => ({
                  value: spec.id,
                  label: `${spec.label}${spec.origin === 'custom' ? '（カスタム）' : ''}`,
                  iconNode: spec.ratio ? <AspectRatioIcon ratio={spec.ratio} /> : undefined,
                  tag: spec.sizeTag?.text,
                  tagVariant: spec.sizeTag?.variant,
                })),
              ]}
            />
            {specLoading ? <span className={styles.helperText}>画像種別を読み込み中…</span> : null}
          </div>
        </div>
        <div className="gen-option-group">
          <span className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            アスペクト比
            <HelpTooltip contentKey="aspectRatio" ariaLabel="アスペクト比のヘルプ" />
          </span>
          <div className={styles.aspectRatioButtons}>
            {!effectiveHideUnselectedRatio && (
              <button
                type="button"
                className={`${styles.aspectRatioNoneButton} ${(!settings.aspect_ratio || settings.aspect_ratio === 'none') ? styles.aspectRatioNoneButtonActive : ''}`}
                onClick={() => {
                  setSettings((prev) => ({ ...prev, aspect_ratio: 'none' }));
                  localStorage.setItem('aspect_ratio', 'none');
                }}
              >
                指定なし
              </button>
            )}
            {presets.map((ratio) => (
              <Tooltip key={ratio} content={ASPECT_RATIO_LABELS[ratio] ?? ratio}>
                <button
                  type="button"
                  className={`${styles.aspectRatioButton} ${settings.aspect_ratio === ratio ? styles.aspectRatioButtonActive : ''}`}
                  onClick={() => {
                    setSettings((prev) => ({ ...prev, aspect_ratio: ratio }));
                    localStorage.setItem('aspect_ratio', ratio);
                  }}
                >
                  <AspectRatioIcon ratio={ratio} />
                </button>
              </Tooltip>
            ))}
            {!effectiveHideCustomAspectSelect && (() => {
              // プリセット外の比率が選択されているときはトリガーに比率を表示して強調する
              const customRatio = [...presets, 'none', ''].includes(settings.aspect_ratio || 'none')
                ? null
                : (settings.aspect_ratio || null);
              // 通常は 3 点アイコン (幅を詰めるため文言は出さない)。プリセット外の比率が
              // 選択されているときだけ比率を表示して強調する
              return (
                <Tooltip content={otherSelectLabel}>
                  <button
                    type="button"
                    className={styles.aspectRatioOtherTrigger}
                    data-active={customRatio ? 'true' : 'false'}
                    aria-label={customRatio ? `${otherSelectLabel}（現在: ${customRatio}）` : otherSelectLabel}
                    aria-haspopup="dialog"
                    aria-expanded={aspectRatioModalOpen}
                    onClick={() => setAspectRatioModalOpen(true)}
                  >
                    {customRatio ? (
                      <>
                        <span className={styles.aspectRatioOtherIcon}>
                          <AspectRatioIcon ratio={customRatio} size={24} />
                        </span>
                        <ChevronIcon open={aspectRatioModalOpen} />
                      </>
                    ) : <ThreeDotsIcon />}
                  </button>
                </Tooltip>
              );
            })()}
            <AspectRatioPickerModal
              isOpen={aspectRatioModalOpen}
              onClose={() => setAspectRatioModalOpen(false)}
              ratios={modalRatios}
              selectedRatio={!settings.aspect_ratio || settings.aspect_ratio === 'none' ? null : settings.aspect_ratio}
              modelCode={activeModelCode}
              modelLabel={modalModelLabel}
              modelTooltip={modalModelTooltip}
              onSelect={(ratio) => {
                setSettings((prev) => ({ ...prev, aspect_ratio: ratio }));
                localStorage.setItem('aspect_ratio', ratio);
              }}
            />
          </div>
          {aspectRatioWarningMessage && (
            <span style={{ fontSize: 12, color: 'rgba(var(--brand-primary-rgb) / 0.7)', textAlign: 'left' }}>
              {aspectRatioWarningMessage}
            </span>
          )}
        </div>
        {/* 自動リサイズ / 自動トリミング (+ ON 時のみ目標解像度入力を展開)
            - GPT Image 2 は指定アスペクト比そのままで出力されるためクロップ発生せず「自動リサイズ」扱い
            - Gemini は出力比が微妙にずれるため「自動トリミング」扱い (従来通り)
            - target_width/height は ON 時にしか使われないので、OFF のときは隠す
            - spec のピクセル指定 or ユーザー入力がある場合のみ W×H 欄を描画
            - 名前は歴史的経緯で auto_trim_resolution のまま (互換性維持) */}
        {!hideAutoTrim && (() => {
          const isOpenAI = activeModelCode.startsWith('gpt-image-');
          const checkboxLabel = isOpenAI
            ? '解像度指定で自動リサイズ'
            : (selectedPlatformId && selectedSpec ? '既定のpx数に自動トリミング' : '解像度指定で自動トリミング');
          const resolutionLabel = isOpenAI ? '縮小リサイズ解像度' : 'トリミング解像度';
          const greyedTooltip = isOpenAI
            ? '選択したアスペクト比で API がその解像度のまま出力するため、リサイズは発動しません'
            : '選択したアスペクト比で API が出力するため、トリミング対象がありません';
          return (
          <div className="gen-option-group">
            <label
              className={`${styles.checkboxLabel} ${styles.autoTrimCheckbox}`}
              style={{
                // グレーアウト条件 = user_aspect == api_output_aspect かつ spec/custom のピクセル目標が無い。
                // チェック操作は常に可能 (ユーザー任意) だが、実際には発動しないことを視覚的に示す。
                ...(autoTrimGreyed ? { opacity: 0.5 } : {}),
              }}
              title={autoTrimGreyed ? greyedTooltip : undefined}
            >
              <input
                type="checkbox"
                checked={settings.auto_trim_resolution !== false}
                onChange={(e) => {
                  // 手動変更フラグ: 以降、同一 spec 内のモデル切替ではデフォルトを再適用しない
                  autoTrimDefaultStateRef.current = markAutoTrimTouched(autoTrimDefaultStateRef.current);
                  setSettings((prev) => ({
                    ...prev,
                    auto_trim_resolution: e.target.checked,
                  }));
                }}
              />
              <span>{checkboxLabel}</span>
            </label>
            {settings.auto_trim_resolution !== false ? (
              <div className={styles.autoTrimResolution} style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 11, color: 'rgba(245, 241, 230, 0.5)' }}>{resolutionLabel}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {(() => {
                      // spec 未選択時 (= ユーザー手入力) かつ両辺が揃ったときに、
                      // W/H から最も近いアスペクト比を自動選択する blur ハンドラ
                      // GPT Image 2 は任意比率をネイティブ対応するため、プリセットと 0.5% 以上
                      // ずれる場合は「指定なし」に落として、API に目標比率そのままを投げる。
                      const maybeAutoSelectAspect = () => {
                        if (selectedSpec) return; // spec 選択時は orientation 追従が担当
                        const w = settings.target_width;
                        const h = settings.target_height;
                        if (!w || !h) return;
                        const targetRatio = w / h;
                        const nearest = findNearestAspectRatio(activeModelCode, targetRatio);
                        const isOpenAI = activeModelCode.startsWith('gpt-image-');
                        if (isOpenAI && nearest) {
                          const [nw, nh] = nearest.split(':').map(Number);
                          const nearestRatio = Number.isFinite(nw) && Number.isFinite(nh) && nh > 0 ? nw / nh : null;
                          const deviation = nearestRatio !== null
                            ? Math.abs(targetRatio - nearestRatio) / nearestRatio
                            : Infinity;
                          if (deviation > 0.005) {
                            // 既定プリセットに合わないので「指定なし」へ (API に目標比率そのままで発行)
                            if (settings.aspect_ratio !== 'none') {
                              setSettings((prev) => ({ ...prev, aspect_ratio: 'none' }));
                              localStorage.setItem('aspect_ratio', 'none');
                            }
                            return;
                          }
                        }
                        if (nearest && nearest !== settings.aspect_ratio) {
                          setSettings((prev) => ({ ...prev, aspect_ratio: nearest }));
                          localStorage.setItem('aspect_ratio', nearest);
                        }
                      };
                      return (
                        <>
                          <input
                            type="number"
                            min={1}
                            max={8192}
                            value={settings.target_width ?? ''}
                            placeholder="幅"
                            onChange={(e) => {
                              const v = e.target.value ? Math.max(1, Math.min(8192, Number(e.target.value))) : undefined;
                              setSettings((prev) => ({ ...prev, target_width: v }));
                            }}
                            onBlur={maybeAutoSelectAspect}
                            style={{ width: 64, padding: '6px 6px', fontSize: 13, borderRadius: 6, border: '1px solid rgba(var(--brand-primary-rgb) / 0.25)', background: 'rgba(12, 12, 12, 0.6)', color: '#f5f1e6' }}
                          />
                          <span style={{ color: 'rgba(245, 241, 230, 0.5)' }}>×</span>
                          <input
                            type="number"
                            min={1}
                            max={8192}
                            value={settings.target_height ?? ''}
                            placeholder="高さ"
                            onChange={(e) => {
                              const v = e.target.value ? Math.max(1, Math.min(8192, Number(e.target.value))) : undefined;
                              setSettings((prev) => ({ ...prev, target_height: v }));
                            }}
                            onBlur={maybeAutoSelectAspect}
                            style={{ width: 64, padding: '6px 6px', fontSize: 13, borderRadius: 6, border: '1px solid rgba(var(--brand-primary-rgb) / 0.25)', background: 'rgba(12, 12, 12, 0.6)', color: '#f5f1e6' }}
                          />
                        </>
                      );
                    })()}
                    <span style={{ fontSize: 11, color: 'rgba(245, 241, 230, 0.35)' }}>px</span>
                    {(() => {
                      // 出力倍率 (×1/×2): fixed spec (両辺 px 指定) のときのみ表示。
                      // Retina 等の高解像度書き出し用に、規格寸法の整数倍を目標 px にワンタップで設定する。
                      // ×2 が予測 AI 出力に収まらない場合はアップスケール (引き伸ばし) になるため disabled にし、
                      // 解像度を上げれば選べることをツールチップで示す。
                      if (!selectedSpec || selectedSpec.size_constraint_type !== 'fixed') return null;
                      const specW = selectedSpec.width_px;
                      const specH = selectedSpec.height_px;
                      if (!specW || !specH) return null;
                      const currentSize = settings.image_size || '1K';
                      const api = getApiOutputPixelSize(activeModelCode, settings.aspect_ratio, currentSize);
                      // 媒体が規格寸法と同じ画像しか受け付けない場合 (Google ディスプレイ アップロード型 等) は ×2 を選べない
                      const doubleAllowed = (selectedSpec as { allow_double_output?: boolean }).allow_double_output !== false;
                      // 現在の解像度で収まらないときは、収まる最小の解像度 (長辺昇順) を探す。
                      // 見つかればクリックで解像度を引き上げて目標を設定し、トーストで知らせる (無効化しない)
                      const sizeThatFits = (w: number, h: number): string | null => {
                        const out = getApiOutputPixelSize(activeModelCode, settings.aspect_ratio, currentSize);
                        if (!out || (w <= out.width && h <= out.height)) return currentSize;
                        const sorted = (modelResolutions ?? [])
                          .filter((s) => getModelResolutionEdge(activeModelCode, s) != null)
                          .sort((a, b) => getModelResolutionEdge(activeModelCode, a)! - getModelResolutionEdge(activeModelCode, b)!);
                        for (const s of sorted) {
                          const o = getApiOutputPixelSize(activeModelCode, settings.aspect_ratio, s);
                          if (o && w <= o.width && h <= o.height) return s;
                        }
                        return null;
                      };
                      return (
                        <span style={{ display: 'inline-flex', gap: 4, marginLeft: 6 }}>
                          {[1, 2].map((factor) => {
                            const w = specW * factor;
                            const h = specH * factor;
                            const fits = !api || (w <= api.width && h <= api.height);
                            const neededSize = fits ? currentSize : sizeThatFits(w, h);
                            const enabled = factor === 1 || (doubleAllowed && neededSize !== null);
                            const active = settings.target_width === w && settings.target_height === h;
                            const title = factor === 1
                              ? `規格寸法 (${specW}×${specH}px) で出力`
                              : !doubleAllowed
                                ? 'この媒体は規格寸法と同じ画像しか入稿できません (2倍サイズ非対応)'
                                : fits
                                  ? `規格寸法の2倍 (${w}×${h}px) で出力 (Retina等の高解像度用)`
                                  : neededSize
                                    ? `規格寸法の2倍 (${w}×${h}px) で出力。解像度を${neededSize}に引き上げます`
                                    : 'どの解像度でも AI 出力サイズに収まりません';
                            return (
                              <button
                                key={factor}
                                type="button"
                                disabled={!enabled}
                                title={title}
                                onClick={() => {
                                  setSettings((prev) => ({ ...prev, target_width: w, target_height: h }));
                                  if (neededSize && neededSize !== currentSize) {
                                    applyImageSize(neededSize);
                                    toast.show(`解像度を${currentSize}→${neededSize}に引き上げました`);
                                  }
                                }}
                                style={{
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  borderRadius: 12,
                                  cursor: enabled ? 'pointer' : 'not-allowed',
                                  border: active
                                    ? '1px solid rgba(var(--brand-primary-rgb) / 0.5)'
                                    : '1px solid rgba(245, 241, 230, 0.15)',
                                  background: active ? 'rgba(var(--brand-primary-rgb) / 0.2)' : 'transparent',
                                  color: active
                                    ? 'var(--brand-primary)'
                                    : enabled ? 'rgba(245, 241, 230, 0.55)' : 'rgba(245, 241, 230, 0.25)',
                                }}
                              >
                                ×{factor}
                              </button>
                            );
                          })}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  // API 出力枠 (外側) と 「API 出力から切り抜かれる領域」(内側 / 黄色) をプレビュー。
                  // 実際の backend 挙動: target_ratio にそろえるため片方の辺を維持したまま余剰側をクロップ、
                  // その後 target ピクセルにリサイズ。内枠は「縮尺前のクロップ領域」を表す。
                  const api = getApiOutputPixelSize(activeModelCode, settings.aspect_ratio, settings.image_size);
                  const rawW = settings.target_width;
                  const rawH = settings.target_height;
                  if (!api) return null;
                  if (!rawW && !rawH) return null; // 両方空 → プレビュー非表示
                  // 片方だけ入力されている場合は API アスペクト比から補完してプレビューする
                  // (この場合、アスペクト比は一致するので「リサイズのみ」扱いになる)
                  const tW = rawW ?? Math.round((rawH as number) * (api.width / api.height));
                  const tH = rawH ?? Math.round((rawW as number) * (api.height / api.width));

                  const sourceRatio = api.width / api.height;
                  const targetRatio = tW / tH;
                  // ±0.5% 以内でアスペクト一致 → クロップ発生なし (リサイズのみ)
                  // 片側補完した場合もここで ratiosMatch=true となり「リサイズのみ」表示になる
                  const ratiosMatch = Math.abs(sourceRatio - targetRatio) / targetRatio < 0.005;
                  // GPT Image 2 は指定アスペクト比そのままで出力されクロップが発生しないため、
                  // アスペクト不一致でも常に「リサイズのみ」表示に倒す
                  const isOpenAIPreview = activeModelCode.startsWith('gpt-image-');
                  const showAsNoCrop = ratiosMatch || isOpenAIPreview;
                  // 切り抜き領域 (API 出力ピクセル単位)
                  let keptW: number;
                  let keptH: number;
                  if (sourceRatio > targetRatio) {
                    // source が横長すぎ → 高さは全て残し、幅のみクロップ
                    keptH = api.height;
                    keptW = api.height * targetRatio;
                  } else {
                    // source が縦長すぎ or 同じ → 幅は全て残し、高さのみクロップ
                    keptW = api.width;
                    keptH = api.width / targetRatio;
                  }

                  const MAX_DISPLAY = 120;
                  const scale = MAX_DISPLAY / Math.max(api.width, api.height);
                  const outerW = Math.max(12, Math.round(api.width * scale));
                  const outerH = Math.max(12, Math.round(api.height * scale));
                  const innerW = Math.max(3, Math.round(keptW * scale));
                  const innerH = Math.max(3, Math.round(keptH * scale));
                  const upscaling = tW > keptW || tH > keptH;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div
                        style={{
                          position: 'relative',
                          width: outerW,
                          height: outerH,
                          border: '1px solid rgba(245, 241, 230, 0.35)',
                          background: 'rgba(245, 241, 230, 0.04)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title={showAsNoCrop
                          ? `API 出力 ${api.width}×${api.height} → リサイズのみ ${tW}×${tH} (クロップなし)`
                          : `API 出力 ${api.width}×${api.height} → 切り抜き ${Math.round(keptW)}×${Math.round(keptH)} → リサイズ ${tW}×${tH}${upscaling ? ' (アップスケール)' : ''}`}
                      >
                        {showAsNoCrop ? (
                          <span style={{ fontSize: 10, color: 'rgba(245, 241, 230, 0.55)', textAlign: 'center', padding: 4, lineHeight: 1.3 }}>
                            {isOpenAIPreview ? 'リサイズのみ' : 'トリミングなし'}
                          </span>
                        ) : (() => {
                          // AI 出力 (元画像) を分母としたカット率。センタークロップなので左右/上下それぞれ半分。
                          // 例: AI 出力 1536×1024 → target 1200×628 のとき、(1024-803.77)/1024 ≈ 21.5% → 上下 11%ずつ
                          const cropHorizontalPct = api.width > 0 ? ((api.width - keptW) / api.width) * 100 : 0;
                          const cropVerticalPct = api.height > 0 ? ((api.height - keptH) / api.height) * 100 : 0;
                          const cropLabel = cropHorizontalPct > 0.5
                            ? `左右 ${Math.round(cropHorizontalPct / 2)}%ずつカット`
                            : cropVerticalPct > 0.5
                              ? `上下 ${Math.round(cropVerticalPct / 2)}%ずつカット`
                              : null;
                          // 通常 (クロップ) は黄色、upscale 時はもう少し控えめなサーモン色に
                          const borderColor = upscaling ? 'rgba(230, 160, 106, 0.7)' : '#f5c93f';
                          const bgColor = upscaling ? 'rgba(230, 160, 106, 0.1)' : 'rgba(245, 201, 63, 0.14)';
                          const textColor = upscaling ? 'rgba(230, 160, 106, 0.95)' : '#f5c93f';
                          return (
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: innerW,
                                height: innerH,
                                border: `1.5px solid ${borderColor}`,
                                background: bgColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                              }}
                            >
                              {cropLabel && (
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 600,
                                    color: textColor,
                                    textShadow: '0 0 2px rgba(0, 0, 0, 0.5)',
                                    textAlign: 'center',
                                    lineHeight: 1.5,
                                    padding: '0 1px',
                                    wordBreak: 'keep-all',
                                  }}
                                >
                                  {cropLabel}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <span style={{ fontSize: 10, color: 'rgba(245, 241, 230, 0.4)', textAlign: 'center', lineHeight: 1.3 }}>
                        AI出力: {api.width}×{api.height}
                      </span>
                    </div>
                  );
                })()}
              </div>
            ) : null}
          </div>
          );
        })()}
        </div>
        {/* 右列: モデル / 解像度 / 出力形式 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="gen-option-group">
            <span className="gen-option-group-label">モデル</span>
            <ImageModelFamilySelector
              models={imageModels}
              activeModelCode={activeModelCode}
              onSelect={(code) => selectImageModelByCode(code, settings, setSettings)}
              // モデル固有の細かい設定: 思考レベル (Gemini 標準 / スピード選択時のみ)。
              // Gemini の分割ボタンの右隣に、見出しより一段小さい plain セレクトで置く。
              // 選択肢と既定はモデル別 (THINKING_LEVELS_BY_MODEL: 標準=3 段階・既定 medium / スピード=2 段階・既定 minimal)。
              // defaultValue=モデルの既定 で既定値では「指定なし」扱い (リセット ✕) を出さない
              geminiTrailing={supportsThinkingLevel(activeModelCode) && onThinkingLevelChange ? (
                <HierarchicalSelect
                  variant="plain"
                  label="思考レベル"
                  defaultValue={getDefaultThinkingLevel(activeModelCode)}
                  options={getThinkingLevelOptions(activeModelCode).map((o) => ({ id: `thinking-${o.key}`, key: o.key, label: o.label, type: 'option' as const }))}
                  value={resolveThinkingLevelForModel(activeModelCode, thinkingLevel)}
                  // 不明な値 (リセット経由の 'none' 等) やモデル非対応の値は、そのモデルの既定に倒す
                  onChange={(key) => onThinkingLevelChange(resolveThinkingLevelForModel(activeModelCode, key))}
                />
              ) : undefined}
            />
          </div>
          {hasModelResolutions && (
            <div className="gen-option-group">
              <span className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                解像度
                <HelpTooltip contentKey="resolution" ariaLabel="解像度のヘルプ" />
              </span>
              <div className={styles.optionGroupButtons}>
                {(() => {
                  // 解像度ラベル → 規定解像度の長辺近似 (backend/services/generation_service.py の STANDARD_RESOLUTIONS と同期)
                  const MAX_EDGE: Record<string, number> = { '1K': 1280, '2K': 1920, '4K': 3840 };
                  // spec が要求する最小長辺 (幅・高さのどちらかがこれ以上必要)
                  const specRequiredEdge = Math.max(
                    selectedSpec?.width_px ?? selectedSpec?.min_width_px ?? 0,
                    selectedSpec?.height_px ?? selectedSpec?.min_height_px ?? 0,
                  );
                  return [
                    { value: '1K', label: '1K' },
                    { value: '2K', label: '2K' },
                    { value: '4K', label: '4K' },
                  ]
                    // モデルが supported_resolutions に含めている解像度のみ表示
                    // (512px は 2026-10-08 に廃止。旧「スピードモード専用」判定も撤去し、
                    //  supported_resolutions だけで出し分ける)
                    .filter((option) => modelResolutions!.includes(option.value))
                    .map((option) => {
                      const isSpeedMode = settings.generation_model === GENERATION_MODEL_SPEED;
                      // spec 要求解像度に対してこの選択肢が生成する画像が小さすぎるか
                      const tooSmallForSpec =
                        specRequiredEdge > 0 && MAX_EDGE[option.value] < specRequiredEdge;
                      const isDisabled = tooSmallForSpec;
                      const isActive = (settings.image_size || '1K') === option.value;
                      const isInvalidSelection = isDisabled && isActive;
                      const tooltipMessage = tooSmallForSpec
                        ? `出力画像が要求する ${specRequiredEdge}px に足りないため、この解像度では生成できません`
                        : '';
                      const button = (
                        <button
                          key={option.value}
                          type="button"
                          className={`${styles.optionButton} ${isActive ? styles.optionButtonActive : ''} ${isInvalidSelection ? styles.optionButtonWarning : ''}`}
                          disabled={isDisabled}
                          onClick={() => {
                            setSettings((prev) => ({
                              ...prev,
                              image_size: option.value,
                            }));
                            const sizeKey = isSpeedMode ? 'image_size_speed' : 'image_size_quality';
                            localStorage.setItem(sizeKey, option.value);
                          }}
                        >
                          {option.label}
                        </button>
                      );
                      return isDisabled ? (
                        <Tooltip key={option.value} content={tooltipMessage}>
                          {button}
                        </Tooltip>
                      ) : (
                        button
                      );
                    });
                })()}
              </div>
              {recommendedShortfall && (() => {
                const r = recommendedShortfall;
                const cur = settings.image_size || '1K';
                const recLabel = r.recommended.longestEdgeOnly
                  ? `推奨長辺${r.recommended.width}px`
                  : `推奨サイズ${r.recommended.width}×${r.recommended.height}px`;
                // 「この媒体の推奨サイズは1440×2560pxです」の形 (「推奨サイズ」「推奨長辺」の直後に「は」を入れる)
                const recSentence = r.recommended.longestEdgeOnly
                  ? `この媒体の推奨長辺は${r.recommended.width}pxです`
                  : `この媒体の推奨サイズは${r.recommended.width}×${r.recommended.height}pxです`;
                const pred = `約${r.predicted.width}×${r.predicted.height}px`;
                const isMaxBound = !!getSpecGuardBounds(selectedSpec)?.maxWidth || !!getSpecGuardBounds(selectedSpec)?.maxHeight;
                let text = '';
                let info = false;
                let action: { label: string; onClick: () => void } | null = null;
                if (r.kind === 'target_below_recommended') {
                  text = `リサイズ目標 (${r.target!.width}×${r.target!.height}px) が${recLabel}より小さいため、解像度を上げても推奨には届きません。`;
                  const rec = getSpecRecommendedSize(selectedSpec);
                  if (rec && !r.recommended.longestEdgeOnly) {
                    action = {
                      label: '推奨に合わせる',
                      onClick: () => setSettings((prev) => ({ ...prev, target_width: rec.width, target_height: rec.height })),
                    };
                  }
                } else if (r.kind === 'output_below_recommended') {
                  text = r.suggestedSize
                    ? `${recSentence}。${cur}で生成される画像 (${pred}) は最低条件は満たしますが推奨には届きません。`
                    : isMaxBound
                      ? `${recSentence}が、最大サイズに収まる解像度では推奨に届きません。`
                      : `${recLabel}には、このモデルではどの解像度でも届きません。`;
                } else if (r.kind === 'upscale_below_recommended') {
                  text = `${recLabel}に合わせて拡大されます。${cur}で生成される画像 (加工前 ${pred}) は推奨より小さいため、画質が落ちます。`
                    + (r.suggestedSize ? '' : isMaxBound ? ' この媒体の最大サイズ内では推奨に届く解像度がありません。' : ' このモデルでは推奨サイズに届く解像度がありません。');
                } else {
                  info = true;
                  text = `指定した目標サイズ${r.target!.width}×${r.target!.height}pxへ拡大されます (${cur}で生成される画像は${pred})。媒体の${recLabel}は満たしています。`;
                }
                if (r.suggestedSize && !action) {
                  const next = r.suggestedSize;
                  action = { label: `${next}にする`, onClick: () => applyImageSize(next) };
                }
                return (
                  <div className={`${styles.recommendedNote} ${info ? styles.recommendedNoteInfo : ''}`} role={info ? 'note' : 'alert'}>
                    <span style={{ flex: 1, minWidth: 0 }}>{text}</span>
                    {action && (
                      <button type="button" className={styles.recommendedNoteAction} onClick={action.onClick}>
                        {action.label}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          <div className="gen-option-group">
            <span className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              出力形式
              <HelpTooltip contentKey="outputFormat" ariaLabel="出力形式のヘルプ" />
            </span>
            <div className={styles.optionGroupButtons} ref={formatMenuContainerRef}>
              <button
                type="button"
                className={`${styles.optionButton} ${currentFormat === undefined ? styles.optionButtonActive : ''}`}
                onClick={() => {
                  setSettings((prev) => ({ ...prev, output_format: undefined, image_background: undefined }));
                  setOpenFormatMenu(null);
                }}
              >
                指定なし
              </button>
              {ALL_IMAGE_OUTPUT_FORMATS.filter((format) => format !== 'webp' || !webpCompact).map((format) => {
                const isSupported = modelSupportedFormats.includes(format);
                const isActive = currentFormat === format;
                const isTransparencyFormat = format === 'png' || format === 'webp';
                const modelAllowsTransparency = isTransparencyCapableModel(activeModelCode);
                const showDropdown = isTransparencyFormat && modelAllowsTransparency && isSupported;
                const fmtLabel = format === 'webp' ? 'WebP' : format.toUpperCase();
                const activeLabel = isActive && currentBackground === 'transparent' ? `透過${fmtLabel}` : fmtLabel;

                if (!showDropdown) {
                  const button = (
                    <button
                      key={format}
                      type="button"
                      className={`${styles.optionButton} ${isActive ? styles.optionButtonActive : ''}`}
                      disabled={!isSupported}
                      onClick={() => {
                        setSettings((prev) => ({ ...prev, output_format: format, image_background: undefined }));
                      }}
                    >
                      {fmtLabel}
                    </button>
                  );
                  return !isSupported ? (
                    <Tooltip key={format} content="選択中のモデルではこの出力形式に対応していません">
                      {button}
                    </Tooltip>
                  ) : (
                    button
                  );
                }

                const menuKey = format as 'png' | 'webp';
                const isMenuOpen = openFormatMenu === menuKey;
                // モデル選択 (ImageModelFamilySelector) と同じ「1 ピル + 区切り線なしのシェブロン」
                return (
                  <div key={format} className={`${styles.splitFormatButton} ${styles.splitFormatPill} ${isActive ? styles.splitFormatPillActive : ''}`}>
                    <button
                      type="button"
                      className={styles.splitFormatMain}
                      aria-pressed={isActive}
                      onClick={() => {
                        setSettings((prev) => ({ ...prev, output_format: format }));
                        setOpenFormatMenu(null);
                      }}
                    >
                      {activeLabel}
                    </button>
                    <button
                      type="button"
                      className={styles.splitFormatChevron}
                      aria-label={`${fmtLabel} の背景オプション`}
                      aria-haspopup="menu"
                      aria-expanded={isMenuOpen}
                      onClick={() => setOpenFormatMenu(isMenuOpen ? null : menuKey)}
                    >
                      <ChevronIcon open={isMenuOpen} />
                    </button>
                    {isMenuOpen && (
                      // メニューの見た目はモデル選択 (ImageModelFamilySelector) の品質メニューと同じ
                      // 共通 .dropdown / .option + modal-surface を使う
                      <div className={`${msStyles.dropdown} modal-surface ${styles.formatMenu}`} role="menu">
                        {(['opaque', 'transparent'] as const).map((bg) => {
                          const isBgActive = isActive && (bg === 'transparent'
                            ? currentBackground === 'transparent'
                            : currentBackground !== 'transparent');
                          const itemLabel = bg === 'transparent' ? `透過${fmtLabel}` : fmtLabel;
                          return (
                            <div
                              key={bg}
                              role="menuitemradio"
                              aria-checked={isBgActive}
                              className={`${msStyles.option} ${styles.formatMenuOption} ${isBgActive ? msStyles.optionSelected : ''}`}
                              onClick={() => {
                                setSettings((prev) => ({
                                  ...prev,
                                  output_format: format,
                                  image_background: bg === 'transparent' ? 'transparent' : undefined,
                                }));
                                setOpenFormatMenu(null);
                              }}
                            >
                              {itemLabel}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {webpCompact && (() => {
                // 行幅が足りないときは WebP を 3 点ボタンのメニューに格納して行幅を詰める。
                // 選択中は 3 点の代わりに "WebP" / "透過WebP" を表示して強調する。
                const format = 'webp' as const;
                const isSupported = modelSupportedFormats.includes(format);
                const isActive = currentFormat === format;
                const allowTransparent = isTransparencyCapableModel(activeModelCode) && isSupported;
                const isMenuOpen = openFormatMenu === format;
                const activeLabel = isActive && currentBackground === 'transparent' ? '透過WebP' : 'WebP';
                const trigger = (
                  <button
                    type="button"
                    className={`${styles.optionButton} ${styles.formatMoreButton} ${isActive ? styles.optionButtonActive : ''}`}
                    disabled={!isSupported}
                    aria-label="その他の出力形式"
                    aria-haspopup="menu"
                    aria-expanded={isMenuOpen}
                    onClick={() => setOpenFormatMenu(isMenuOpen ? null : format)}
                  >
                    {isActive ? activeLabel : <ThreeDotsIcon />}
                  </button>
                );
                return (
                  <div className={`${styles.splitFormatButton} ${styles.formatMoreWrap}`}>
                    {!isSupported ? (
                      <Tooltip content="選択中のモデルではこの出力形式に対応していません">{trigger}</Tooltip>
                    ) : (
                      trigger
                    )}
                    {isMenuOpen && (
                      <div className={`${msStyles.dropdown} modal-surface ${styles.formatMenu}`} role="menu">
                        {(allowTransparent ? (['opaque', 'transparent'] as const) : (['opaque'] as const)).map((bg) => {
                          const isBgActive = isActive && (bg === 'transparent'
                            ? currentBackground === 'transparent'
                            : currentBackground !== 'transparent');
                          return (
                            <div
                              key={bg}
                              role="menuitemradio"
                              aria-checked={isBgActive}
                              className={`${msStyles.option} ${styles.formatMenuOption} ${isBgActive ? msStyles.optionSelected : ''}`}
                              onClick={() => {
                                setSettings((prev) => ({
                                  ...prev,
                                  output_format: format,
                                  image_background: bg === 'transparent' ? 'transparent' : undefined,
                                }));
                                setOpenFormatMenu(null);
                              }}
                            >
                              {bg === 'transparent' ? '透過WebP' : 'WebP'}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        </div>
        {(currentMenuGuidelines.length > 0 || currentSpecGuidelines.length > 0
          || (complianceMasterEnabled && candidateRulesets.length > 0)) && (
          <div className={styles.guidelineChipGroup}>
            {renderGuidelineChip('メニュー規定', currentMenuGuidelines)}
            {renderGuidelineChip('個別規定', currentSpecGuidelines)}
            {complianceMasterEnabled && candidateRulesets.map((rule) => {
              const on = isRuleOn(rule.code);
              const auto = autoActiveCodes.has(rule.code);
              const overridden = typeof userOverrides[rule.code] === 'boolean'
                && userOverrides[rule.code] !== auto;
              const tooltipBody = on
                ? `${rule.name} の指示を生成プロンプトに挿入します。クリックで OFF に切り替えできます。`
                : `${rule.name} の指示は挿入されません。クリックで ON に切り替えできます。`;
              const tooltipItems = overridden
                ? [tooltipBody, `(自動判定: ${auto ? 'ON' : 'OFF'} を上書き中)`]
                : [tooltipBody];
              return (
                <ListTooltip key={rule.code} items={tooltipItems} title={rule.name}>
                  <button
                    type="button"
                    className={`${styles.guidelineChip} ${styles.guidelineChipToggle} ${on ? styles.guidelineChipToggleOn : styles.guidelineChipToggleOff}`}
                    onClick={() => handleToggleRule(rule.code)}
                    aria-pressed={on}
                  >
                    <span className={styles.guidelineChipCheck} aria-hidden="true">
                      ✓
                    </span>
                    {rule.name}
                  </button>
                </ListTooltip>
              );
            })}
          </div>
        )}
        {!headerless && (
          <div className={styles.accordionPanelFooter}>
            <Tooltip content="閉じる">
              <button
                type="button"
                className={styles.iconButton}
                onClick={onClose}
              >
                <img src="/move_collapse_vertical.svg" alt="閉じる" style={{ width: '16px', height: '16px' }} />
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={currentCompliancePrompt === 'text'}
        title="テキストを含めない設定の推奨"
        message="選択した媒体は画像本体にテキストを焼き込まないことが推奨されています。「テキストを含めない」を ON にしますか？"
        confirmLabel="ON にする"
        cancelLabel="そのまま"
        variant="info"
        onConfirm={acceptTextCompliancePrompt}
        onCancel={dismissCurrentCompliancePrompt}
      />
      <ConfirmDialog
        isOpen={currentCompliancePrompt === 'logo'}
        title="ロゴを追加しない設定の推奨"
        message="選択した媒体は画像本体にロゴを焼き込まないことが推奨されています。「ロゴを追加する」を OFF にしますか？"
        confirmLabel="OFF にする"
        cancelLabel="そのまま"
        variant="info"
        onConfirm={acceptLogoCompliancePrompt}
        onCancel={dismissCurrentCompliancePrompt}
      />
    </div>
  );
}
